import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { RoleAssignmentDto, ResponseDto } from "@mimber/schema"
import { roleAssignmentsKeys, rolesKeys } from "../roles.utils"
import type { RevokeRoleAssignmentParams } from "./useRevokeRoleAssignment.types"

/**
 * Organization-specific hook for revoking a role assignment from a member
 * Can only be used in mimber-organization app
 */
export const useRevokeRoleAssignment = (
  roleUuid: string,
  options?: UseMutationOptions<
    ResponseDto<RoleAssignmentDto>,
    Error,
    RevokeRoleAssignmentParams
  >
): UseMutationResult<
  ResponseDto<RoleAssignmentDto>,
  Error,
  RevokeRoleAssignmentParams
> => {
  guardAppUsage("mimber-organization", "useRevokeRoleAssignment")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ assignmentUuid }: RevokeRoleAssignmentParams) => {
      const response = await server.delete<ResponseDto<RoleAssignmentDto>>(
        `roles/${roleUuid}/assignments/${assignmentUuid}`
      )
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: roleAssignmentsKeys.findByRole(roleUuid),
      })
      queryClient.invalidateQueries({
        queryKey: rolesKeys.findOne(roleUuid),
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
